"use client";

import { clsx, Slot } from "@trydig/ui";
import { useControllableState } from "@trydig/ui/hooks";
import {
	type ComponentProps,
	createContext,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	use,
	useId,
} from "react";
import styles from "./ShowMore.module.css";

type ShowMoreProps = ComponentProps<"div"> & {
	open?: boolean;
	onOpenChange?: Dispatch<SetStateAction<boolean>>;
};

interface ShowMoreContext {
	id: string;
	open: boolean;
	setOpen: (value: boolean) => void;
}

const ShowMoreContext = createContext<ShowMoreContext>(null!);

const useResolvedId = (id?: string) => {
	const internalId = useId();

	return id ?? internalId;
};

export const ShowMoreRoot = ({
	children,
	className,
	open,
	onOpenChange,
	...rest
}: ShowMoreProps) => {
	const id = useResolvedId(rest.id);
	const [showMore, setShowMore] = useControllableState(
		false,
		open,
		onOpenChange,
	);

	return (
		<ShowMoreContext.Provider
			value={{ open: showMore, setOpen: setShowMore, id }}
		>
			<div
				data-state={showMore ? "open" : "closed"}
				data-target={id}
				className={clsx(className, "show-more-root")}
				{...rest}
			>
				{children}
			</div>
		</ShowMoreContext.Provider>
	);
};

export const ShowMoreContent = ({
	className,
	fade,
	children,
	collapsedHeight: maxHeight,
	...props
}: ComponentProps<"div"> & {
	fade?: boolean;
	collapsedHeight?: number | string;
}) => {
	const { open, id } = use(ShowMoreContext);

	return (
		<div
			id={id}
			data-state={open ? "open" : "closed"}
			className={clsx(
				styles.root,
				className,

				"show-more-content",
			)}
			{...props}
			style={
				{
					...props.style,
					"--collapsed-height":
						typeof maxHeight === "number"
							? `${maxHeight}px`
							: maxHeight || undefined,
				} as React.CSSProperties
			}
		>
			{children}
		</div>
	);
};

export const ShowMoreTrigger = ({
	children,
	...rest
}: PropsWithChildren<ComponentProps<"button">>) => {
	const { open, setOpen, id } = use(ShowMoreContext);

	return (
		<Slot
			aria-expanded={open}
			aria-controls={id}
			data-open={open}
			onClick={() => setOpen(!open)}
			{...rest}
		>
			{children}
		</Slot>
	);
};
